import React, { useState, useEffect } from 'react';
import { Save, Copy, CheckCircle, Server, Key, Info } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACSConfig = () => {
  const [config, setConfig] = useState({
    acs_url: process.env.REACT_APP_ACS_URL || 'http://103.179.252.243:7547',
    acs_username: 'acs',
    acs_password: 'acsJoss123',
    connection_request_username: 'acs',
    connection_request_password: 'acsJoss123',
    notes: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [copiedField, setCopiedField] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/acs/config`);
      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ACS config:', error);
      toast.error('Gagal memuat konfigurasi ACS');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/acs/config`, config);
      if (response.data.success) {
        toast.success('Konfigurasi ACS berhasil disimpan');
      }
    } catch (error) {
      console.error('Error saving ACS config:', error);
      toast.error('Gagal menyimpan konfigurasi ACS');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      toast.success(`${fieldName} berhasil di-copy!`);
      setTimeout(() => setCopiedField(''), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-2 dark:text-slate-100">
              <Server className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
              Konfigurasi ACS (TR-069)
            </h1>
            <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">
              Kelola konfigurasi ACS untuk setup perangkat OLT
            </p>
          </div>
          <Button
            onClick={() => setShowInfoDialog(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Info className="w-4 h-4 mr-2" />
            Lihat Info
          </Button>
        </div>
      </div>

      {/* Config Form */}
      <Card className="shadow-md">
        <CardContent className="p-6">
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
            {/* ACS URL */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Server className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                ACS URL
              </Label>
              <Input
                type="text"
                value={config.acs_url}
                onChange={(e) => setConfig({ ...config, acs_url: e.target.value })}
                placeholder="http://103.179.252.243:7547"
                required
              />
            </div>

            {/* ACS Credentials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Key className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  ACS Username
                </Label>
                <Input
                  type="text"
                  value={config.acs_username}
                  onChange={(e) => setConfig({ ...config, acs_username: e.target.value })}
                  placeholder="admin"
                  required
                />
              </div>
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Key className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  ACS Password
                </Label>
                <Input
                  type="text"
                  value={config.acs_password}
                  onChange={(e) => setConfig({ ...config, acs_password: e.target.value })}
                  placeholder="password"
                  required
                />
              </div>
            </div>

            {/* Connection Request Credentials */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-slate-900 mb-3 dark:text-slate-100">Connection Request Authentication</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Key className="w-4 h-4 text-green-600 dark:text-green-400" />
                    Connection Request Username
                  </Label>
                  <Input
                    type="text"
                    value={config.connection_request_username}
                    onChange={(e) => setConfig({ ...config, connection_request_username: e.target.value })}
                    placeholder="admin"
                    required
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Key className="w-4 h-4 text-green-600 dark:text-green-400" />
                    Connection Request Password
                  </Label>
                  <Input
                    type="text"
                    value={config.connection_request_password}
                    onChange={(e) => setConfig({ ...config, connection_request_password: e.target.value })}
                    placeholder="password"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="mb-2">Catatan (Opsional)</Label>
              <textarea
                value={config.notes || ''}
                onChange={(e) => setConfig({ ...config, notes: e.target.value })}
                placeholder="Catatan tambahan tentang konfigurasi ACS..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 min-h-[80px]"
              />
            </div>

            {/* Save Button */}
            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Menyimpan...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Simpan Konfigurasi
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Info Dialog */}
      <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-indigo-900">
              📋 Info Konfigurasi ACS (TR-069)
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* ACS URL */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 dark:bg-slate-900 dark:border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-slate-700 dark:text-slate-200">ACS URL</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(config.acs_url, 'ACS URL')}
                  className="text-xs"
                >
                  {copiedField === 'ACS URL' ? (
                    <><CheckCircle className="w-3 h-3 mr-1" /> Copied</>
                  ) : (
                    <><Copy className="w-3 h-3 mr-1" /> Copy</>
                  )}
                </Button>
              </div>
              <div className="font-mono text-sm bg-white p-2 rounded border border-slate-300 dark:bg-slate-800 dark:border-slate-600">
                {config.acs_url}
              </div>
            </div>

            {/* ACS Username */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 dark:bg-slate-900 dark:border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-slate-700 dark:text-slate-200">ACS Username</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(config.acs_username, 'ACS Username')}
                  className="text-xs"
                >
                  {copiedField === 'ACS Username' ? (
                    <><CheckCircle className="w-3 h-3 mr-1" /> Copied</>
                  ) : (
                    <><Copy className="w-3 h-3 mr-1" /> Copy</>
                  )}
                </Button>
              </div>
              <div className="font-mono text-sm bg-white p-2 rounded border border-slate-300 dark:bg-slate-800 dark:border-slate-600">
                {config.acs_username}
              </div>
            </div>

            {/* ACS Password */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 dark:bg-slate-900 dark:border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-slate-700 dark:text-slate-200">ACS Password</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(config.acs_password, 'ACS Password')}
                  className="text-xs"
                >
                  {copiedField === 'ACS Password' ? (
                    <><CheckCircle className="w-3 h-3 mr-1" /> Copied</>
                  ) : (
                    <><Copy className="w-3 h-3 mr-1" /> Copy</>
                  )}
                </Button>
              </div>
              <div className="font-mono text-sm bg-white p-2 rounded border border-slate-300 dark:bg-slate-800 dark:border-slate-600">
                {config.acs_password}
              </div>
            </div>

            {/* Connection Request Username */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 dark:bg-green-900/30">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-green-700 dark:text-green-300">Connection Request Username</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(config.connection_request_username, 'Connection Request Username')}
                  className="text-xs"
                >
                  {copiedField === 'Connection Request Username' ? (
                    <><CheckCircle className="w-3 h-3 mr-1" /> Copied</>
                  ) : (
                    <><Copy className="w-3 h-3 mr-1" /> Copy</>
                  )}
                </Button>
              </div>
              <div className="font-mono text-sm bg-white p-2 rounded border border-green-300 dark:bg-slate-800">
                {config.connection_request_username}
              </div>
            </div>

            {/* Connection Request Password */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 dark:bg-green-900/30">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-green-700 dark:text-green-300">Connection Request Password</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(config.connection_request_password, 'Connection Request Password')}
                  className="text-xs"
                >
                  {copiedField === 'Connection Request Password' ? (
                    <><CheckCircle className="w-3 h-3 mr-1" /> Copied</>
                  ) : (
                    <><Copy className="w-3 h-3 mr-1" /> Copy</>
                  )}
                </Button>
              </div>
              <div className="font-mono text-sm bg-white p-2 rounded border border-green-300 dark:bg-slate-800">
                {config.connection_request_password}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4 dark:bg-blue-900/30">
              <h4 className="font-semibold text-blue-900 mb-2">📝 Cara Penggunaan:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Copy setiap field yang diperlukan menggunakan tombol Copy</li>
                <li>Paste ke konfigurasi TR-069 di perangkat OLT Anda</li>
                <li>Pastikan perangkat OLT dapat mengakses URL ACS</li>
                <li>Tunggu beberapa saat hingga perangkat terdaftar di ACS</li>
              </ol>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ACSConfig;
